/** The playing surface of a record: grooves, land areas, lead-in and run-out. */

import type * as THREE from 'three';
import { TAU } from '../lib/math.ts';
import { mulberry32 } from '../lib/random.ts';
import { canvas, ctx2d, memo, tex } from './canvas.ts';

/** Label radius and outer edge, as fractions of the disc radius. */
export const LABEL_R = 0.335;
export const EDGE_R = 0.985;

type Renderer = THREE.WebGLRenderer | null;

const ring = (g: CanvasRenderingContext2D, R: number, r: number): void => {
  g.beginPath();
  g.arc(R, R, r, 0, TAU);
  g.stroke();
};

/** Colour map for the playing surface. */
export function vinylSurfaceMap(size = 1024, renderer: Renderer = null): THREE.CanvasTexture {
  return memo(`vinyl-surf-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c);
    const R = size / 2;

    g.fillStyle = '#07070a';
    g.fillRect(0, 0, size, size);

    /* Base sheen: vinyl reads slightly warmer toward the middle. */
    const bg = g.createRadialGradient(R, R, R * 0.1, R, R, R);
    bg.addColorStop(0, '#131318');
    bg.addColorStop(0.55, '#0d0d11');
    bg.addColorStop(1, '#08080b');
    g.fillStyle = bg;
    g.beginPath();
    g.arc(R, R, R, 0, TAU);
    g.fill();

    /* Grooves: fine bright rings at a ~4px period, so mipmaps and anisotropic
       filtering resolve them instead of moiring. */
    g.lineWidth = 1;
    const rand = mulberry32(7);
    for (let r = R * LABEL_R + 2; r < R * EDGE_R; r += 2) {
      const band = Math.sin(r * 0.055) * 0.5 + 0.5; /* slow loudness variation */
      g.strokeStyle = `rgba(214,224,236,${0.055 + band * 0.075 + rand() * 0.02})`;
      ring(g, R, r);
      g.strokeStyle = 'rgba(0,0,0,0.55)';
      ring(g, R, r + 1);
    }

    /* Track gaps — the darker rings between songs. */
    for (const f of [0.44, 0.58, 0.71, 0.83, 0.92]) {
      g.strokeStyle = 'rgba(255,255,255,0.16)';
      g.lineWidth = 1.6;
      ring(g, R, R * f);
      g.strokeStyle = 'rgba(0,0,0,0.75)';
      g.lineWidth = 3.4;
      ring(g, R, R * f + 2.6);
    }

    /* Smooth lead-in area at the rim. */
    g.globalCompositeOperation = 'destination-out';
    g.lineWidth = R * 0.012;
    g.strokeStyle = 'rgba(0,0,0,0.35)';
    ring(g, R, R * 0.992);
    g.globalCompositeOperation = 'source-over';

    /* Rolled edge highlight. */
    g.lineWidth = 3;
    g.strokeStyle = 'rgba(190,205,220,0.28)';
    ring(g, R, R * 0.997);

    return tex(c, { srgb: true, aniso: 16, renderer });
  });
}

/**
 * Normal map for the grooves — the radial derivative of a sine groove profile.
 * The profile depends only on radius, so it is precomputed into a lookup table
 * rather than calling cos() a million times.
 */
export function vinylNormalMap(size = 1024, renderer: Renderer = null): THREE.CanvasTexture {
  return memo(`vinyl-norm-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c, { willReadFrequently: true });
    const img = g.createImageData(size, size);
    const d = img.data;
    const R = size / 2;
    const FREQ = size / 8; /* ~4px groove period */
    /* Peak slope of the groove wall. Keep this small: a large value tips the
       normals almost tangential, turning the whole disc into specular haze. */
    const SLOPE = 0.42;

    const lut = new Float32Array(R + 2);
    for (let i = 0; i < lut.length; i++) {
      const rn = i / R;
      if (rn <= LABEL_R || rn >= EDGE_R) continue;
      /* Fade out at both ends so the lead-in and run-out read as smooth land. */
      const fade = Math.min(1, (rn - LABEL_R) / 0.05) * Math.min(1, (EDGE_R - rn) / 0.03);
      lut[i] = Math.cos(rn * FREQ * TAU) * SLOPE * fade;
    }

    for (let y = 0; y < size; y++) {
      const dy = y - R + 0.5;
      const dy2 = dy * dy;
      for (let x = 0; x < size; x++) {
        const dx = x - R + 0.5;
        const r = Math.sqrt(dx * dx + dy2);
        let nx = 0;
        let ny = 0;
        if (r > 1) {
          const deriv = lut[r | 0] ?? 0;
          if (deriv !== 0) {
            nx = -deriv * (dx / r);
            ny = -deriv * (dy / r);
          }
        }
        const len = Math.sqrt(nx * nx + ny * ny + 1);
        const i = (y * size + x) * 4;
        d[i] = (nx / len) * 127.5 + 127.5;
        d[i + 1] = (ny / len) * 127.5 + 127.5;
        d[i + 2] = (1 / len) * 127.5 + 127.5;
        d[i + 3] = 255;
      }
    }
    g.putImageData(img, 0, 0);
    return tex(c, { srgb: false, aniso: 16, renderer });
  });
}

/** Roughness: the grooved area scatters more than the smooth label and rim. */
export function vinylRoughnessMap(size = 512, renderer: Renderer = null): THREE.CanvasTexture {
  return memo(`vinyl-rough-${size}`, () => {
    const c = canvas(size);
    const g = ctx2d(c);
    const R = size / 2;
    g.fillStyle = '#303030'; /* smooth land: glossy */
    g.fillRect(0, 0, size, size);
    const gr = g.createRadialGradient(R, R, R * LABEL_R, R, R, R * EDGE_R);
    gr.addColorStop(0, '#585858'); /* grooved band scatters more */
    gr.addColorStop(0.5, '#6b6b6b');
    gr.addColorStop(1, '#545454');
    g.fillStyle = gr;
    g.beginPath();
    g.arc(R, R, R * EDGE_R, 0, TAU);
    g.arc(R, R, R * LABEL_R, 0, TAU, true);
    g.fill();
    return tex(c, { renderer });
  });
}
